import { SortType } from '../providers/SearchProfilesPage/types'
import { transformToQuery } from '../utils/query'
import { NUMBER_PER_PROFILE_REQUEST } from '../constants/search'
import { PracticeProfile, PracticeSpecialist } from '../types/practice'
import { Keywords, Percentage, ProfileReview } from '../types/reviews'
import apiRequest from './request'

const basePath = process.env.NEXT_PUBLIC_BASIC_API_URL

export const getPracticeBySlug = async ({
  lang,
  slug
}: {
  lang: string
  slug: string
}): Promise<{
  result: PracticeProfile
  statusCode?: number
  message?: string
}> => apiRequest(`${basePath}/${lang}/practices/${slug}`).then((r) => r.json())

export const getSponsoredPracticeBySlug = async ({
  lang,
  city,
  keyword
}: {
  lang: string
  city: string
  keyword: string
}): Promise<{
  data: PracticeProfile[]
}> => apiRequest(`${basePath}/${lang}/search/sponsored/practice/${city}/${keyword}`).then((r) => r.json())

export const getSuggestedPractice = async ({
  lang,
  specialties,
  distance,
  type,
  lat,
  lon
}: {
  lang: string
  specialties: string[]
  distance: string
  type: string
  lat: string
  lon: string
}): Promise<{
  data: PracticeProfile[]
}> => {
  const query = transformToQuery({
    specialties,
    lat,
    lon,
    distance,
    type: type !== 'practices' ? type : ''
  })
  return apiRequest(`${basePath}/${lang}/search/suggested?${query}&selected=practice`).then((r) => r.json())
}

export const getPracticeReviews = async ({
  id,
  tenant,
  language,
  offset = 0,
  limit = 10,
  sort = 'desc',
  search = '',
  keyword = '',
  rating,
  replies
}: {
  id: number | string
  tenant: string
  language: string
  offset?: number
  limit?: number
  sort?: string
  search?: string
  keyword?: string
  rating?: number[]
  replies?: number
}): Promise<{
  result: ProfileReview[]
  totalCount: number
  percentage: Percentage
  keywordNames: Keywords[]
}> => {
  const query = transformToQuery({
    practiceId: id,
    offset,
    limit,
    sort,
    search,
    rating,
    keyword,
    language,
    replies: !!replies
  })
  return apiRequest(`${basePath}/${tenant}/reviews/?${query}`)
    .then((r) => r.json())
    .then((r) => r || {})
}

export const getPracticeLatestReview = async ({
  id,
  tenant,
  language
}: {
  id: number | string
  tenant: string
  language: string
}): Promise<ProfileReview> =>
  apiRequest(`${basePath}/${tenant}/reviews/?practiceId=${id}&latest=true&language=${language}`)
    .then((r) => r.json())
    .then((r) => r.result)

export const getPracticeSpecialists = async ({
  slug,
  lang,
  offset = 0,
  limit = 4,
  sort = 'popular',
  search = '',
  keyword
}: {
  slug: string
  lang: string
  offset?: number
  limit?: number
  sort?: string
  search?: string
  keyword?: string
}): Promise<{
  result: PracticeSpecialist[]
  totalCount: number
}> => {
  const query = transformToQuery({
    limit,
    offset,
    sort,
    search,
    keyword
  })
  return apiRequest(`${basePath}/${lang}/practices/${slug}/specialists?${query}`)
    .then((r) => r.json())
    .then((r) => r)
}

export const getSearchPractices = async ({
  lang,
  specialties,
  lat,
  lon,
  page,
  distance,
  sortType,
  expand = true,
  premium = false,
  locationType,
  type = ''
}: {
  lang: string
  specialties?: string[]
  lat: string
  lon: string
  distance: string
  page: number
  sortType: SortType
  expand?: boolean
  locationType?: string | null
  type?: string
  premium?: boolean
}): Promise<{
  data: PracticeProfile[]
  total: number
  distance: string
}> => {
  const filters: Record<string, number | string | string[] | boolean | null | undefined> = {
    specialties,
    distance,
    sortType,
    lat,
    lon,
    limit: NUMBER_PER_PROFILE_REQUEST,
    offset: (page - 1) * NUMBER_PER_PROFILE_REQUEST,
    expand,
    locationType,
    type,
    premium
  }

  const query = transformToQuery(filters)

  return apiRequest(`${basePath}/${lang}/search/practices?${query}`).then((r) => r.json())
}
